import { Link } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import SEO from '../components/SEO';
import AdUnit from '../components/AdUnit';
import HeroCanvas from '../components/HeroCanvas';
import ScrollReveal from '../components/ScrollReveal';
import API_BASE_URL from '../config';

var learnGuides = [
  { slug: 'llm-comparison', title: 'Top LLMs Compared', desc: 'GPT-4o vs Claude vs Gemini vs Llama vs DeepSeek — benchmarks, pricing, and practical guidance.', tags: ['Benchmarks', 'Pricing', 'Architecture'] },
  { slug: 'how-llms-work', title: 'How LLMs Work', desc: 'Understand transformers, self-attention, tokenization, training phases, and scaling laws from the ground up.', tags: ['Transformers', 'Attention', 'Training'] },
  { slug: 'rag-explained', title: 'RAG Explained', desc: 'Deep dive into retrieval-augmented generation — chunking, vector search, re-ranking, and production patterns.', tags: ['Retrieval', 'Vector search', 'Pipelines'] },
];

var featuredTools = [
  { path: '/json-formatter', title: 'JSON Formatter', desc: 'Format & validate JSON instantly' },
  { path: '/password-generator', title: 'Password Generator', desc: 'Strong random passwords' },
  { path: '/loan-calculator', title: 'Loan Calculator', desc: 'Monthly payments & interest' },
  { path: '/word-counter', title: 'Word Counter', desc: 'Words, chars & read time' },
  { path: '/base64', title: 'Base64 Tool', desc: 'Encode & decode instantly' },
  { path: '/color-converter', title: 'Color Converter', desc: 'HEX, RGB & HSL conversion' },
];

var trustPoints = [
  { title: 'AI Guides That Go Deep', text: 'Architecture, code, diagrams, and real-world patterns — written for people who build things.' },
  { title: 'Your Data Stays on Your Device', text: 'All tools run client-side. No uploads, no tracking, no server processing.' },
  { title: 'Zero Friction, Zero Cost', text: 'No accounts, no email gates, no paywalls. Open and start using immediately.' },
];

/* Animated counter */
function AnimatedCounter({ target, suffix }) {
  var [count, setCount] = useState(0);
  var [started, setStarted] = useState(false);
  var ref = useRef(null);
  var numTarget = parseInt(target, 10);

  useEffect(function () {
    var el = ref.current;
    if (!el) return;
    var observer = new IntersectionObserver(function (entries) {
      if (entries[0].isIntersecting) { setStarted(true); observer.disconnect(); }
    }, { threshold: 0.5 });
    observer.observe(el);
    return function () { observer.disconnect(); };
  }, []);

  useEffect(function () {
    if (!started || isNaN(numTarget)) return;
    var duration = 1400;
    var startTime = null;
    function step(ts) {
      if (!startTime) startTime = ts;
      var progress = Math.min((ts - startTime) / duration, 1);
      var eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * numTarget));
      if (progress < 1) requestAnimationFrame(step);
    }
    requestAnimationFrame(step);
  }, [numTarget, started]);

  return <span ref={ref}>{count}{suffix || ''}</span>;
}

function Home() {
  var [newsArticles, setNewsArticles] = useState([]);

  useEffect(function () {
    fetch(API_BASE_URL + '/api/articles?limit=6')
      .then(function (r) { return r.json(); })
      .then(function (data) { setNewsArticles(data.articles || []); })
      .catch(function () {});
  }, []);

  return (
    <div className="page-home">
      <SEO
        title="Learn AI, LLMs, RAG & Free Developer Tools"
        description="Learn how LLMs, RAG, AI agents, and MCP servers work. Plus 15+ free browser-based tools for developers. No sign-up, no fluff."
        path="/"
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'WebSite',
          name: 'Anthroper',
          url: 'https://anthroper.com',
          description: 'Learn how LLMs, RAG, AI agents, and MCP servers work. Plus 15+ free browser-based tools for developers.',
          publisher: { '@type': 'Organization', name: 'Anthroper', url: 'https://anthroper.com' },
        })}</script>
      </Helmet>

      {/* ═══ HERO ═══ */}
      <section className="home-hero">
        <HeroCanvas />
        <div className="container" style={{ position: 'relative', zIndex: 2 }}>
          <ScrollReveal>
            <p className="home-hero-badge">Open-source knowledge for builders</p>
          </ScrollReveal>
          <ScrollReveal delay={100}>
            <h1 className="home-hero-title">
              Learn AI the Way<br />
              <span className="home-hero-accent">It Should Be Taught</span>
            </h1>
          </ScrollReveal>
          <ScrollReveal delay={200}>
            <p className="home-hero-sub">
              Deep dives into LLMs, RAG, agents, prompt engineering, and more — written for builders who want to understand how things actually work.
            </p>
          </ScrollReveal>
          <ScrollReveal delay={300}>
            <div className="home-hero-actions">
              <Link to="/learn" className="btn btn-primary btn-lg">Start Learning</Link>
              <Link to="/tools" className="btn btn-outline btn-lg">Try Free Tools</Link>
              <Link to="/blog" className="btn btn-ghost btn-lg">Read Articles</Link>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* ═══ STATS ═══ */}
      <section className="home-stats">
        <div className="container">
          <div className="home-stats-grid">
            {[
              { value: '12', suffix: '+', label: 'Deep Guides' },
              { value: '15', suffix: '+', label: 'Free Tools' },
              { value: '0', suffix: '', label: 'Paywalls' },
              { value: '100', suffix: '%', label: 'Client-Side' },
            ].map(function (s, i) {
              return (
                <ScrollReveal key={s.label} delay={i * 100}>
                  <div className="home-stat">
                    <span className="home-stat-value"><AnimatedCounter target={s.value} suffix={s.suffix} /></span>
                    <span className="home-stat-label">{s.label}</span>
                  </div>
                </ScrollReveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ═══ LEARN AI — Stacked wide cards ═══ */}
      <section className="home-section home-section--tinted">
        <div className="container">
          <ScrollReveal>
            <h2 className="home-section-title">Learn AI</h2>
            <p className="home-section-sub">In-depth guides on the topics that matter. Diagrams, code, and real-world patterns.</p>
          </ScrollReveal>
          <div className="home-card-stack">
            {learnGuides.map(function (g, i) {
              return (
                <ScrollReveal key={g.slug} delay={i * 60}>
                  <Link to={'/learn/' + g.slug} className="home-wide-card">
                    <h3 className="home-wide-card-title">{g.title}</h3>
                    <div className="home-wide-card-body">
                      <p className="home-wide-card-desc">{g.desc}</p>
                      <div className="home-wide-card-tags">
                        {g.tags.map(function (t, j) {
                          return <span key={j}>{t}{j < g.tags.length - 1 ? ' · ' : ''}</span>;
                        })}
                      </div>
                      <span className="home-wide-card-link">Read guide →</span>
                    </div>
                  </Link>
                </ScrollReveal>
              );
            })}
          </div>
          <ScrollReveal delay={400}>
            <div style={{ textAlign: 'center', marginTop: '2rem' }}>
              <Link to="/learn" className="btn btn-secondary">All 14 Guides →</Link>
            </div>
          </ScrollReveal>
          <AdUnit />
        </div>
      </section>

      {/* ═══ LATEST ARTICLES — 1 featured + 3 row + 2 row ═══ */}
      <section className="home-section">
        <div className="container">
          <ScrollReveal>
            <h2 className="home-section-title">Latest Articles</h2>
            <p className="home-section-sub">News and insights on tech, business, and security.</p>
          </ScrollReveal>

          {/* Featured first article */}
          {newsArticles.length > 0 && (
            <ScrollReveal>
              <Link to={'/blog/' + newsArticles[0].slug} className="blog-featured">
                {newsArticles[0].image && (
                  <div className="blog-featured-img-wrap">
                    <img src={newsArticles[0].image} alt={newsArticles[0].title} className="blog-featured-img" loading="lazy" />
                    <div className="blog-featured-overlay" />
                  </div>
                )}
                <div className="blog-featured-body">
                  <div className="blog-featured-meta">
                    <span className="article-tag">{newsArticles[0].tag}</span>
                    <span className="article-date">{newsArticles[0].date}</span>
                  </div>
                  <h2 className="blog-featured-title">{newsArticles[0].title}</h2>
                  <p className="blog-featured-excerpt">{newsArticles[0].excerpt}</p>
                  <span className="blog-featured-cta">Read more →</span>
                </div>
              </Link>
            </ScrollReveal>
          )}

          {/* Row of 3 */}
          {newsArticles.length > 1 && (
            <div className="home-articles-row-3">
              {newsArticles.slice(1, 4).map(function (a, i) {
                return (
                  <ScrollReveal key={a.slug} delay={i * 80}>
                    <article className="blog-card">
                      {a.image && (
                        <Link to={'/blog/' + a.slug} className="blog-card-img-link">
                          <img src={a.image} alt={a.title} className="blog-card-img" loading="lazy" />
                          <div className="blog-card-img-overlay" />
                        </Link>
                      )}
                      <div className="blog-card-body">
                        <div className="blog-card-meta">
                          <span className="article-tag">{a.tag}</span>
                          <span className="article-date">{a.date}</span>
                        </div>
                        <h2 className="blog-card-title"><Link to={'/blog/' + a.slug}>{a.title}</Link></h2>
                        <Link to={'/blog/' + a.slug} className="read-more">Read more →</Link>
                      </div>
                    </article>
                  </ScrollReveal>
                );
              })}
            </div>
          )}

          {/* Row of 2 */}
          {newsArticles.length > 4 && (
            <div className="home-articles-row-2">
              {newsArticles.slice(4, 6).map(function (a, i) {
                return (
                  <ScrollReveal key={a.slug} delay={i * 80}>
                    <article className="blog-card">
                      {a.image && (
                        <Link to={'/blog/' + a.slug} className="blog-card-img-link">
                          <img src={a.image} alt={a.title} className="blog-card-img" loading="lazy" />
                          <div className="blog-card-img-overlay" />
                        </Link>
                      )}
                      <div className="blog-card-body">
                        <div className="blog-card-meta">
                          <span className="article-tag">{a.tag}</span>
                          <span className="article-date">{a.date}</span>
                        </div>
                        <h2 className="blog-card-title"><Link to={'/blog/' + a.slug}>{a.title}</Link></h2>
                        <Link to={'/blog/' + a.slug} className="read-more">Read more →</Link>
                      </div>
                    </article>
                  </ScrollReveal>
                );
              })}
            </div>
          )}

          <ScrollReveal delay={400}>
            <div style={{ textAlign: 'center', marginTop: '2rem' }}>
              <Link to="/blog" className="btn btn-secondary">View All Articles</Link>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* ═══ FREE TOOLS — Grid ═══ */}
      <section className="home-section home-section--tinted">
        <div className="container">
          <ScrollReveal>
            <h2 className="home-section-title">Free Online Tools</h2>
            <p className="home-section-sub">15+ tools for developers, writers, and students. Runs in your browser, zero data sent anywhere.</p>
          </ScrollReveal>
          <ScrollReveal>
            <div className="home-tools-grid">
              {featuredTools.map(function (t) {
                return (
                  <Link key={t.path} to={t.path} className="home-tool-card">
                    <h3>{t.title}</h3>
                    <p>{t.desc}</p>
                  </Link>
                );
              })}
            </div>
          </ScrollReveal>
          <ScrollReveal delay={400}>
            <div style={{ textAlign: 'center', marginTop: '2rem' }}>
              <Link to="/tools" className="btn btn-secondary">See All Tools →</Link>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* ═══ WHY ANTHROPER — Trust points ═══ */}
      <section className="home-section">
        <div className="container">
          <ScrollReveal>
            <h2 className="home-section-title" style={{ textAlign: 'center' }}>Why People Choose Anthroper</h2>
          </ScrollReveal>
          <div className="home-trust-grid">
            {trustPoints.map(function (tp, i) {
              return (
                <ScrollReveal key={tp.title} delay={i * 120}>
                  <div className="home-trust-item">
                    <h3>{tp.title}</h3>
                    <p>{tp.text}</p>
                  </div>
                </ScrollReveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ═══ CTA BANNER — Dark rounded card ═══ */}
      <section className="home-section">
        <div className="container">
          <ScrollReveal>
            <div className="home-cta-banner">
              <div className="home-cta-text">
                <h2>Ready to go deeper?</h2>
                <p>Pick a guide and start learning — or grab a tool and get to work.</p>
              </div>
              <div className="home-cta-actions">
                <Link to="/learn" className="btn btn-primary btn-lg">Explore AI Guides</Link>
                <Link to="/tools" className="home-cta-link">Free Tools →</Link>
              </div>
            </div>
          </ScrollReveal>
        </div>
        https://omg10.com/4/10876342
      </section>
    </div>
    
  );
}

export default Home;
